import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { PropertyService } from '../properties/service/property.service';
import { CreateTenantsComponent } from '../create-tenants/create-tenants.component';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { InputSwitchModule } from 'primeng/inputswitch';

@Component({
  selector: 'app-tenants',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    CreateTenantsComponent,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    DynamicDialogModule,
    FormsModule,
    DropdownModule,
    TooltipModule,
    InputSwitchModule
  ],
  templateUrl: './tenants.component.html',
  styleUrl: './tenants.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class TenantsComponent implements OnInit {
  tenants: any[] = [];
  totalRecords = 0;
  currentPage = 0;
  itemsPerPage = 10;
  position: 'center' | 'right' = 'center';
  visible: boolean = false;
  selectedTenant: any | null = null;

  nameOptions: any[] = [];
  emailOptions: any[] = [];
  phoneOptions: any[] = [];

  filters: any = {
    name: '',
    email: '',
    search: '',
  };

  sorting: any = {
    field: '',
    order: 1,
  };

  blockSpecialCharsPattern = /[.*+?^${}()|[\]\\]/g;
  constructor(
    private router: Router,
    private propertyService: PropertyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.loadDropdownOptions();
    // this.fetchTenants({
    //   first: this.currentPage * this.itemsPerPage,
    //   rows: this.itemsPerPage,
    // });
  }

  loadDropdownOptions(): void {
    this.propertyService.getTenants({ page: 1, limit: 100 }).subscribe({
      next: (response) => {
        const allTenants = response.data;

        this.nameOptions = [
          ...new Set(
            allTenants.map(
              (tenant: any) => `${tenant.first_name} ${tenant.last_name}`
            )
          ),
        ].map((name) => ({ label: name, value: name }));

        this.emailOptions = [
          ...new Set(allTenants.map((tenant: any) => tenant.email)),
        ].map((email) => ({ label: email, value: email }));

        this.phoneOptions = [
          ...new Set(allTenants.map((tenant: any) => tenant.phone)),
        ].map((phone) => ({ label: phone, value: phone }));
      },
      error: (err) => console.error('Error loading dropdown options:', err),
    });
  }

  fetchTenants(event: any): void {
    const page = event.first / event.rows + 1;
    const limit = event.rows;

    const queryParams = {
      page,
      limit,
      filters: JSON.stringify(this.filters),
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    };

    this.propertyService.getTenants(queryParams).subscribe({
      next: (response) => {
        this.tenants = response.data;
        this.totalRecords = response.total;
        this.currentPage = page - 1;
      },
      error: (err) => {
        console.error('Error fetching tenants:', err);
      },
    });
  }

  onFilterChange(): void {
    this.fetchTenants({
      first: this.currentPage * this.itemsPerPage,
      rows: this.itemsPerPage,
    });
  }

  resetFilters(): void {
    this.filters = {
      name: '',
      email: '',
      search: '',
    };
    this.onFilterChange();
  }

  onSorting(event: any): void {
    this.sorting.field = event.field;
    this.sorting.order = event.order;
    this.fetchTenants({
      first: this.currentPage * this.itemsPerPage,
      rows: this.itemsPerPage,
    });
  }

  userIndex(user: any): number {
    return this.currentPage * this.itemsPerPage + this.tenants.indexOf(user) + 1;
  }

  showDialogForEdit(position: 'right', tenant: any): void {
    this.position = position;
    this.selectedTenant = tenant;
    this.visible = true;
  }

  showDialogForCreate(position: 'right'): void {
    this.position = position;
    this.selectedTenant = null;
    this.visible = true;
  }

  onDialogClose(): void {
    this.visible = false;
    this.selectedTenant = null;
    this.fetchTenants({
      first: this.currentPage * this.itemsPerPage,
      rows: this.itemsPerPage,
    });
  }

  deleteUser(id: string): void {
    this.propertyService.deteleUserOrTenant(id).subscribe({
      next: () => {
        this.tenants = this.tenants.filter((tenant) => tenant._id !== id);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Tenant deleted successfully',
        });
      },
      error: (err) => {
        console.error('Error deleting tenant:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete tenant',
        });
      },
    });
  }

  togglingStatus: Record<string, boolean> = {};

  toggleTenantStatus(tenant: any, event: any): void {
    const newStatus: boolean = event.checked;
    this.togglingStatus = { ...this.togglingStatus, [tenant._id]: true };
    this.propertyService.updateTenantStatus(tenant._id, newStatus).subscribe({
      next: () => {
        tenant.isActive = newStatus;
        this.togglingStatus = { ...this.togglingStatus, [tenant._id]: false };
        this.messageService.add({
          severity: 'success',
          summary: newStatus ? 'Notifications enabled' : 'Notifications disabled',
          detail: `${tenant.first_name} ${tenant.last_name} will ${newStatus ? 'now receive' : 'no longer receive'} SMS and email`,
        });
      },
      error: (err) => {
        // Revert the optimistic UI state on failure
        tenant.isActive = !newStatus;
        this.togglingStatus = { ...this.togglingStatus, [tenant._id]: false };
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to update tenant status',
        });
      },
    });
  }

  sendingCredentials: Record<string, boolean> = {};

  sendCredentials(tenant: any): void {
    this.sendingCredentials = { ...this.sendingCredentials, [tenant._id]: true };
    this.propertyService.sendTenantCredentials(tenant._id).subscribe({
      next: () => {
        this.sendingCredentials = { ...this.sendingCredentials, [tenant._id]: false };
        this.messageService.add({
          severity: 'success',
          summary: 'Sent',
          detail: `Credentials emailed to ${tenant.email}`,
        });
      },
      error: (err) => {
        this.sendingCredentials = { ...this.sendingCredentials, [tenant._id]: false };
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to send credentials',
        });
      },
    });
  }

  confirmDelete(userId: string): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this tenant?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle text-danger',
      accept: () => {
        this.deleteUser(userId);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Cancelled',
          detail: 'Tenant deletion cancelled',
        });
      },
    });
  }

  // Block on typing
  blockSpecialChars(event: KeyboardEvent) {
    console.log("BLOCK typing");
    if (this.blockSpecialCharsPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  // Block on paste
  blockSpecialCharsOnPaste(event: ClipboardEvent) {
    console.log("BLOCK PASTE");
    const pasteData = event.clipboardData?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(pasteData)) {
      event.preventDefault();
    }
  }

  // Block on drop
  blockSpecialCharsOnDrop(event: DragEvent) {
    console.log("BLOCK Drop");
    const dropData = event.dataTransfer?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(dropData)) {
      event.preventDefault();
    }
  }

  // Final cleanup in case something sneaks in
  sanitizeInput(event: Event) {
    console.log("SANITIZE INPUT");
    const input = event.target as HTMLInputElement;
    if (this.blockSpecialCharsPattern.test(input.value)) {
      input.value = input.value.replace(this.blockSpecialCharsPattern, '');
    }
  }
}
